/**
 * View: Nova proposta — genérica (projeto / venda / serviço).
 * Adapta-se ao tipo via query string `?tipo=<TipoProposta>`.
 *
 * Estrutura comum:
 *  - Cliente (lookup Omie)
 *  - Vendedor (auto-preenchido)
 *  - Validade
 *  - Por tipo: descrição/escopo, tabela de itens, prazo/garantia, observações
 */

import {
  criarPropostaProjetoSchema,
  criarPropostaVendaMaterialSchema,
  criarPropostaVendaServicoSchema,
  type CriarPropostaInput,
} from '@infomatic/shared';

import { api, ApiError } from '../../lib/api.js';
import { getUsuarioLogado } from '../../lib/auth.js';
import { escapeHtml, renderLayout } from '../layout.js';
import { getRouter } from '../router.js';

type TipoSuportado =
  | 'projeto-infra'
  | 'projeto-cftv'
  | 'projeto-controle-acesso'
  | 'venda-pecas'
  | 'venda-computador'
  | 'venda-distribuidor'
  | 'venda-projeto-servico';

const LABELS: Record<TipoSuportado, string> = {
  'projeto-infra': 'Projeto de infraestrutura',
  'projeto-cftv': 'Projeto de CFTV',
  'projeto-controle-acesso': 'Projeto de controle de acesso',
  'venda-pecas': 'Venda de peças',
  'venda-computador': 'Venda de computador',
  'venda-distribuidor': 'Venda — Distribuidor',
  'venda-projeto-servico': 'Projeto/Serviço',
};

const MODELOS_SERVICO = [
  { value: 'migracao-m365', label: 'Migração M365 / e-mail' },
  { value: 'migracao-servidor', label: 'Migração de servidor' },
  { value: 'organizacao-rack', label: 'Organização de rack' },
  { value: 'implantacao-firewall', label: 'Implantação de firewall' },
  { value: 'consultoria-seguranca', label: 'Consultoria de segurança' },
  { value: 'recuperacao-desastre', label: 'Recuperação de desastre' },
  { value: 'personalizado', label: 'Personalizado' },
];

type TipoQualquer = TipoSuportado | 'contrato-recorrente' | 'renovacao-recorrente';

function getTipoFromUrl(): TipoQualquer | null {
  const params = new URLSearchParams(window.location.search);
  const tipo = params.get('tipo');
  if (!tipo) return null;
  if (tipo === 'contrato-recorrente') return 'contrato-recorrente';
  if (tipo === 'renovacao-recorrente') return 'renovacao-recorrente';
  if (tipo in LABELS) return tipo as TipoSuportado;
  return null;
}

export function renderNovaPropostaGenerica(tipo: TipoSuportado): void {
  const $app = document.getElementById('app');
  if (!$app) return;

  const usuario = getUsuarioLogado();
  if (!usuario) {
    $app.innerHTML = renderLayout({
      title: 'Nova proposta',
      activeNav: 'propostas',
      bodyHtml: `<section class="card"><p>Faça login pra criar propostas.</p></section>`,
    });
    return;
  }

  const isProjeto = tipo.startsWith('projeto-');
  const isVendaMaterial = ['venda-pecas', 'venda-computador', 'venda-distribuidor'].includes(tipo);
  const isVendaServico = tipo === 'venda-projeto-servico';

  $app.innerHTML = renderLayout({
    title: 'Nova proposta',
    activeNav: 'propostas',
    bodyHtml: `
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:var(--sp-6)">
        <div>
          <h1 style="font-family:var(--font-display);font-size:var(--fs-3xl);margin:0">Nova proposta</h1>
          <div style="color:var(--color-text-secondary);margin-top:var(--sp-1)">${escapeHtml(LABELS[tipo])}</div>
        </div>
        <a href="/propostas/nova" data-route class="btn btn-secondary">← Trocar tipo</a>
      </div>

      <form id="form-nova-generica" class="form-card" autocomplete="off" novalidate>
        <div id="top-error" class="badge badge-error" style="display:none;margin-bottom:var(--sp-4)"></div>

        <fieldset class="form-section">
          <legend>Cliente</legend>
          <div class="form-row">
            <label class="form-field">
              <span>CNPJ ou CPF</span>
              <div style="display:flex;gap:var(--sp-2)">
                <input type="text" name="cliente.documento" id="g-documento" placeholder="com ou sem pontuação" maxlength="20" autocomplete="off" />
                <button type="button" id="g-btn-omie" class="btn btn-secondary" style="white-space:nowrap">Buscar Omie</button>
              </div>
              <small id="g-documento-help" style="color:var(--color-text-muted)">
                Digite CNPJ (14 dígitos) ou CPF (11) e clique em "Buscar Omie" pra preencher automaticamente.
              </small>
            </label>
          </div>
          <div class="form-row form-row-2">
            <label class="form-field">
              <span>Empresa *</span>
              <input type="text" name="cliente.empresa" id="g-empresa" required maxlength="200" />
            </label>
            <label class="form-field">
              <span>Responsável *</span>
              <input type="text" name="cliente.responsavel" id="g-responsavel" required maxlength="200" />
            </label>
          </div>
          <div class="form-row form-row-2">
            <label class="form-field">
              <span>E-mail(s) *</span>
              <input type="text" name="cliente.email" id="g-email" required maxlength="500"
                placeholder="cliente@empresa.com (vírgula separa múltiplos)" />
              <small class="form-hint">Vários: vírgula, ponto-e-vírgula ou espaço.</small>
            </label>
            <label class="form-field">
              <span>Telefone</span>
              <input type="text" name="cliente.telefone" id="g-telefone" maxlength="30" />
            </label>
          </div>
          <div class="form-row">
            <label class="form-field">
              <span>Validade *</span>
              <input type="text" name="validade" required maxlength="50" placeholder="ex: 15 dias úteis" value="15 dias úteis" />
            </label>
          </div>
        </fieldset>

        ${
          isVendaServico
            ? `<fieldset class="form-section">
                <legend>Modelo do serviço</legend>
                <label class="form-field">
                  <span>Modelo *</span>
                  <select name="modelo" required>
                    ${MODELOS_SERVICO.map((m) => `<option value="${m.value}">${m.label}</option>`).join('')}
                  </select>
                </label>
              </fieldset>`
            : ''
        }

        ${
          isProjeto || isVendaServico
            ? `<fieldset class="form-section">
                <legend>Descrição</legend>
                <label class="form-field">
                  <span>Descrição executiva *</span>
                  <textarea name="descricao" required rows="3" maxlength="2000"></textarea>
                </label>
                <label class="form-field">
                  <span>Escopo detalhado *</span>
                  <textarea name="escopo" required rows="5" maxlength="5000"></textarea>
                </label>
                <div class="form-row form-row-2">
                  <label class="form-field">
                    <span>Prazo de execução *</span>
                    <input type="text" name="prazoExecucao" required maxlength="100" placeholder="ex: 30 dias" />
                  </label>
                  <label class="form-field">
                    <span>Garantia *</span>
                    <input type="text" name="garantia" required maxlength="200" placeholder="ex: 12 meses peças e serviços" />
                  </label>
                </div>
              </fieldset>`
            : ''
        }

        ${
          isProjeto
            ? `<fieldset class="form-section">
                <legend>Materiais</legend>
                <div id="lista-materiais"></div>
                <button type="button" class="btn btn-secondary" data-add="materiais">+ Adicionar material</button>
              </fieldset>`
            : ''
        }

        <fieldset class="form-section">
          <legend>${isVendaMaterial ? 'Itens' : 'Serviços'}</legend>
          <div id="lista-servicos"></div>
          <button type="button" class="btn btn-secondary" data-add="servicos">+ Adicionar ${isVendaMaterial ? 'item' : 'serviço'}</button>
        </fieldset>

        ${
          isVendaMaterial
            ? `<fieldset class="form-section">
                <legend>Entrega e pagamento</legend>
                <div class="form-row form-row-2">
                  <label class="form-field">
                    <span>Forma de pagamento</span>
                    <select name="formaPagamento">
                      <option value="">—</option>
                      <option value="a-vista">À vista</option>
                      <option value="parcelado">Parcelado</option>
                      <option value="cartao">Cartão</option>
                      <option value="boleto">Boleto</option>
                    </select>
                  </label>
                  <label class="form-field">
                    <span>Prazo de entrega</span>
                    <input type="text" name="prazoEntrega" maxlength="100" placeholder="ex: 10 dias úteis" />
                  </label>
                </div>
                <label class="pub-checkbox" style="margin-top:var(--sp-3)">
                  <input type="checkbox" name="freteIncluso" />
                  <span>Frete incluso</span>
                </label>
              </fieldset>`
            : ''
        }

        <fieldset class="form-section">
          <legend>Observações</legend>
          <label class="form-field">
            <span>Observações (opcional)</span>
            <textarea name="observacoes" rows="3" maxlength="5000"></textarea>
          </label>
        </fieldset>

        <div style="display:flex;gap:var(--sp-3);margin-top:var(--sp-5)">
          <button type="submit" class="btn btn-primary" id="btn-submit">Criar proposta</button>
          <a href="/propostas" data-route class="btn btn-secondary">Cancelar</a>
        </div>
        <div style="margin-top:var(--sp-3);font-size:var(--fs-sm);color:var(--color-text-secondary)">
          Total estimado: <strong id="total-estimado">R$ 0,00</strong>
        </div>
      </form>
    `,
  });

  injectStyles();
  initItemRows(isProjeto, isVendaMaterial);
  wireForm(tipo, usuario.email, usuario.nome ?? usuario.email);
}

interface ItemRow {
  descricao: string;
  quantidade: number;
  unidade: string;
  valorUnitario: number;
}

const state = {
  materiais: [] as ItemRow[],
  servicos: [] as ItemRow[],
};

function initItemRows(isProjeto: boolean, _isVendaMaterial: boolean): void {
  state.materiais = [];
  state.servicos = [];

  if (isProjeto) addRow('materiais');
  addRow('servicos');

  document.querySelectorAll<HTMLButtonElement>('[data-add]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const tipo = btn.dataset.add as 'materiais' | 'servicos';
      addRow(tipo);
    });
  });
}

function addRow(tipo: 'materiais' | 'servicos'): void {
  state[tipo].push({
    descricao: '',
    quantidade: 1,
    unidade: tipo === 'materiais' ? 'UN' : 'h',
    valorUnitario: 0,
  });
  renderRows(tipo);
}

function removeRow(tipo: 'materiais' | 'servicos', idx: number): void {
  state[tipo].splice(idx, 1);
  renderRows(tipo);
}

function renderRows(tipo: 'materiais' | 'servicos'): void {
  const $lista = document.getElementById(`lista-${tipo}`);
  if (!$lista) return;
  $lista.innerHTML = state[tipo]
    .map(
      (row, i) => `
      <div class="item-row" data-idx="${i}" data-tipo="${tipo}">
        <div class="item-desc-wrap">
          <input type="text" placeholder="Digite pra buscar no Omie..." data-field="descricao" value="${escapeHtml(row.descricao)}" autocomplete="off" />
          <div class="item-suggest" data-suggest hidden></div>
        </div>
        <input type="number" placeholder="Qtd" data-field="quantidade" min="0.01" step="0.01" value="${row.quantidade}" />
        <input type="text" placeholder="Un." data-field="unidade" value="${escapeHtml(row.unidade)}" maxlength="10" />
        <input type="number" placeholder="Valor R$" data-field="valorUnitario" min="0" step="0.01" value="${row.valorUnitario}" />
        <span class="item-total" data-total>R$ ${(row.quantidade * row.valorUnitario).toFixed(2)}</span>
        <button type="button" class="btn-remove" title="Remover">✕</button>
      </div>
    `,
    )
    .join('');

  $lista.querySelectorAll<HTMLElement>('.item-row').forEach((row) => {
    const idx = Number(row.dataset.idx);
    const t = row.dataset.tipo as 'materiais' | 'servicos';
    row.querySelectorAll<HTMLInputElement>('input').forEach((input) => {
      input.addEventListener('input', () => {
        const item = state[t][idx];
        if (!item) return;
        const field = input.dataset.field;
        if (field === 'descricao') {
          item.descricao = input.value;
          debouncedSuggest(t, idx, input.value, row);
        } else if (field === 'unidade') item.unidade = input.value;
        else if (field === 'quantidade') item.quantidade = Number(input.value) || 0;
        else if (field === 'valorUnitario') item.valorUnitario = Number(input.value) || 0;
        const $tot = row.querySelector('[data-total]');
        if ($tot) $tot.textContent = `R$ ${(item.quantidade * item.valorUnitario).toFixed(2)}`;
        atualizarTotal();
      });
    });
    // Esconde dropdown ao clicar fora
    const $descInput = row.querySelector<HTMLInputElement>('input[data-field="descricao"]');
    if ($descInput) {
      $descInput.addEventListener('blur', () => {
        // delay pra permitir click no item
        setTimeout(() => {
          const $sug = row.querySelector<HTMLElement>('[data-suggest]');
          if ($sug) $sug.hidden = true;
        }, 200);
      });
    }
    row.querySelector('.btn-remove')?.addEventListener('click', () => removeRow(t, idx));
  });

  atualizarTotal();
}

let suggestTimeout: ReturnType<typeof setTimeout> | null = null;
let suggestRequestId = 0;

function debouncedSuggest(
  tipo: 'materiais' | 'servicos',
  idx: number,
  query: string,
  row: HTMLElement,
): void {
  if (suggestTimeout) clearTimeout(suggestTimeout);
  const $sug = row.querySelector<HTMLElement>('[data-suggest]');
  if (!$sug) return;

  if (query.trim().length < 2) {
    $sug.hidden = true;
    return;
  }

  // Já mostra o dropdown com loading — não some entre teclas
  $sug.hidden = false;
  if ($sug.children.length === 0) {
    $sug.innerHTML = '<div class="item-suggest-empty">Buscando...</div>';
  }
  const requestId = ++suggestRequestId;

  suggestTimeout = setTimeout(() => {
    void (async () => {
      try {
        if (tipo === 'materiais') {
          const { produtos } = await api.clientes.buscarProdutos(query);
          if (requestId !== suggestRequestId) return; // request obsoleto
          if (produtos.length === 0) {
            $sug.hidden = false;
            $sug.innerHTML =
              '<div class="item-suggest-empty">Nenhum produto encontrado no Omie. Pode preencher manualmente.</div>';
            return;
          }
          renderSuggest(
            $sug,
            produtos.map((p) => ({
              id: p.id,
              codigo: p.codigo,
              descricao: p.descricao,
              unidade: p.unidade,
              valorUnitario: p.valorUnitario,
              estoqueFisico: p.estoqueFisico,
              estoqueReservado: p.estoqueReservado,
              estoqueDisponivel: p.estoqueDisponivel,
            })),
            tipo,
            idx,
          );
        } else {
          const { servicos } = await api.clientes.buscarServicos(query);
          if (requestId !== suggestRequestId) return;
          if (servicos.length === 0) {
            $sug.hidden = false;
            $sug.innerHTML =
              '<div class="item-suggest-empty">Nenhum serviço encontrado no Omie. Pode preencher manualmente.</div>';
            return;
          }
          renderSuggest(
            $sug,
            servicos.map((s) => ({
              id: s.id,
              codigo: s.codigo,
              descricao: s.descricao,
              unidade: 'h',
              valorUnitario: s.valorUnitario,
            })),
            tipo,
            idx,
          );
        }
      } catch (err) {
        console.warn('[catalogo-omie] erro', err);
        $sug.hidden = false;
        $sug.innerHTML =
          '<div class="item-suggest-empty">Erro ao consultar Omie. Pode preencher manualmente.</div>';
      }
    })();
  }, 300);
}

interface Sugestao {
  id: string;
  codigo: string;
  descricao: string;
  unidade: string;
  valorUnitario: number;
  estoqueFisico?: number;
  estoqueReservado?: number;
  estoqueDisponivel?: number;
}

function estoqueClass(disponivel: number): string {
  if (disponivel <= 0) return 'estoque-zero';
  if (disponivel < 5) return 'estoque-baixo';
  return 'estoque-ok';
}

function renderSuggest(
  $sug: HTMLElement,
  itens: Sugestao[],
  tipo: 'materiais' | 'servicos',
  idx: number,
): void {
  if (itens.length === 0) {
    $sug.hidden = true;
    return;
  }
  $sug.hidden = false;
  $sug.innerHTML = itens
    .slice(0, 10)
    .map((it, i) => {
      const temEstoque = it.estoqueDisponivel !== undefined;
      const estoqueLabel = temEstoque
        ? `<span class="item-suggest-estoque ${estoqueClass(it.estoqueDisponivel ?? 0)}">Disp: ${it.estoqueDisponivel} · Fís: ${it.estoqueFisico} · Res: ${it.estoqueReservado}</span>`
        : '';
      return `
      <div class="item-suggest-row" data-i="${i}">
        <div class="item-suggest-desc">${escapeHtml(it.descricao)}</div>
        <div class="item-suggest-meta">${escapeHtml(it.codigo)} · R$ ${it.valorUnitario.toFixed(2)} ${estoqueLabel}</div>
      </div>
    `;
    })
    .join('');
  $sug.querySelectorAll<HTMLElement>('.item-suggest-row').forEach((el) => {
    el.addEventListener('mousedown', (e) => {
      e.preventDefault();
      const i = Number(el.dataset.i);
      const item = itens[i];
      const stateItem = state[tipo][idx];
      if (!item || !stateItem) return;
      stateItem.descricao = item.descricao;
      stateItem.valorUnitario = item.valorUnitario;
      if (item.unidade) stateItem.unidade = item.unidade;
      renderRows(tipo);
    });
  });
}
function atualizarTotal(): void {
  const total =
    state.materiais.reduce((acc, r) => acc + r.quantidade * r.valorUnitario, 0) +
    state.servicos.reduce((acc, r) => acc + r.quantidade * r.valorUnitario, 0);
  const $el = document.getElementById('total-estimado');
  if ($el) $el.textContent = `R$ ${total.toFixed(2).replace('.', ',')}`;
}

function wireForm(tipo: TipoSuportado, vendedorEmail: string, vendedorNome: string): void {
  const form = document.getElementById('form-nova-generica') as HTMLFormElement | null;
  if (!form) return;

  // Botão Buscar Omie
  const $btnOmie = document.getElementById('g-btn-omie') as HTMLButtonElement | null;
  const $doc = document.getElementById('g-documento') as HTMLInputElement | null;
  if ($btnOmie && $doc) {
    $btnOmie.addEventListener('click', () => {
      void lookupOmieGenerico($doc.value);
    });
  }

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    void onSubmit(form, tipo, vendedorEmail, vendedorNome);
  });
}

async function lookupOmieGenerico(docRaw: string): Promise<void> {
  const doc = docRaw.replace(/\D/g, '');
  const $help = document.getElementById('g-documento-help');
  if (!$help) return;

  if (doc.length !== 11 && doc.length !== 14) {
    $help.textContent = 'Documento inválido — informe CPF (11 dígitos) ou CNPJ (14 dígitos).';
    $help.style.color = 'var(--color-danger)';
    return;
  }

  $help.textContent = 'Buscando no Omie…';
  $help.style.color = 'var(--color-text-muted)';

  try {
    const { cliente } = await api.clientes.lookupOmie(doc);
    setVal('g-empresa', cliente.empresa);
    setVal('g-responsavel', cliente.responsavel);
    setVal('g-email', cliente.email);
    if (cliente.telefone) setVal('g-telefone', cliente.telefone);
    $help.textContent = `OK — ${cliente.empresa} carregado do Omie.`;
    $help.style.color = 'var(--color-success)';
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      $help.textContent = 'Cliente não encontrado no Omie. Preencha manualmente.';
    } else {
      $help.textContent = `Falha ao consultar Omie: ${err instanceof Error ? err.message : 'erro desconhecido'}`;
    }
    $help.style.color = 'var(--color-danger)';
  }
}

function labelCampo(path: string): string | undefined {
  const map: Record<string, string> = {
    'cliente.empresa': 'Empresa',
    'cliente.responsavel': 'Responsável',
    'cliente.email': 'E-mail do cliente',
    'cliente.cnpj': 'CNPJ',
    'cliente.cpf': 'CPF',
    validade: 'Validade',
    'payload.descricao': 'Descrição',
    'payload.escopo': 'Escopo',
    'payload.prazoExecucao': 'Prazo de execução',
    'payload.garantia': 'Garantia',
    'payload.itens': 'Itens',
    'payload.servicos': 'Serviços',
    'payload.materiais': 'Materiais',
    'payload.modelo': 'Modelo do serviço',
  };
  return map[path];
}

function setVal(id: string, value: string): void {
  const el = document.getElementById(id) as HTMLInputElement | null;
  if (el) el.value = value;
}

async function onSubmit(
  form: HTMLFormElement,
  tipo: TipoSuportado,
  vendedorEmail: string,
  vendedorNome: string,
): Promise<void> {
  const $err = document.getElementById('top-error');
  if ($err) $err.style.display = 'none';

  const fd = new FormData(form);
  const documento = String(fd.get('cliente.documento') ?? '').replace(/\D/g, '');
  const cliente: Record<string, unknown> = {
    empresa: String(fd.get('cliente.empresa') ?? '').trim(),
    responsavel: String(fd.get('cliente.responsavel') ?? '').trim(),
    email: String(fd.get('cliente.email') ?? '')
      .trim()
      .toLowerCase(),
    telefone: String(fd.get('cliente.telefone') ?? '').trim() || undefined,
  };
  if (documento.length === 14) cliente.cnpj = documento;
  else if (documento.length === 11) cliente.cpf = documento;

  const itensMateriais = state.materiais
    .filter((r) => r.descricao.trim().length > 0 && r.quantidade > 0)
    .map((r) => ({
      descricao: r.descricao.trim(),
      unidade: r.unidade.trim() || undefined,
      quantidade: r.quantidade,
      valorUnitario: r.valorUnitario,
      valorTotal: Number((r.quantidade * r.valorUnitario).toFixed(2)),
    }));
  const itensServicos = state.servicos
    .filter((r) => r.descricao.trim().length > 0 && r.quantidade > 0)
    .map((r) => ({
      descricao: r.descricao.trim(),
      horasEstimadas: r.quantidade,
      valorUnitario: r.valorUnitario,
      valorTotal: Number((r.quantidade * r.valorUnitario).toFixed(2)),
    }));

  let payload: unknown;
  let schema:
    | typeof criarPropostaProjetoSchema
    | typeof criarPropostaVendaMaterialSchema
    | typeof criarPropostaVendaServicoSchema;

  if (tipo.startsWith('projeto-')) {
    schema = criarPropostaProjetoSchema;
    payload = {
      descricao: String(fd.get('descricao') ?? '').trim(),
      escopo: String(fd.get('escopo') ?? '').trim(),
      materiais: itensMateriais,
      servicos: itensServicos.map((s) => ({
        ...s,
        valorUnitario: s.valorUnitario,
        valorTotal: s.valorTotal,
      })),
      prazoExecucao: String(fd.get('prazoExecucao') ?? '').trim(),
      garantia: String(fd.get('garantia') ?? '').trim(),
      observacoes: String(fd.get('observacoes') ?? '').trim() || undefined,
    };
  } else if (tipo === 'venda-projeto-servico') {
    schema = criarPropostaVendaServicoSchema;
    payload = {
      modelo: String(fd.get('modelo') ?? 'personalizado'),
      descricao: String(fd.get('descricao') ?? '').trim(),
      escopo: String(fd.get('escopo') ?? '').trim(),
      servicos: itensServicos,
      garantia: String(fd.get('garantia') ?? '').trim() || undefined,
      observacoes: String(fd.get('observacoes') ?? '').trim() || undefined,
    };
  } else {
    schema = criarPropostaVendaMaterialSchema;
    payload = {
      itens: itensServicos.map((s) => ({
        descricao: s.descricao,
        quantidade: s.horasEstimadas ?? 1,
        valorUnitario: s.valorUnitario,
        valorTotal: s.valorTotal,
      })),
      formaPagamento: String(fd.get('formaPagamento') ?? '') || undefined,
      prazoEntrega: String(fd.get('prazoEntrega') ?? '').trim() || undefined,
      freteIncluso: fd.get('freteIncluso') === 'on',
      observacoes: String(fd.get('observacoes') ?? '').trim() || undefined,
    };
  }

  const dto = {
    tipo,
    cliente,
    vendedor: { nome: vendedorNome, email: vendedorEmail },
    validade: String(fd.get('validade') ?? '15 dias úteis').trim(),
    payload,
    observacoes: String(fd.get('observacoes') ?? '').trim() || undefined,
  };

  const parsed = schema.safeParse(dto);
  if (!parsed.success) {
    if ($err) {
      $err.style.display = 'block';
      const campos = parsed.error.errors
        .map((e) => labelCampo(e.path.join('.')) ?? e.path.join('.') ?? 'campo')
        .filter((v, i, a) => a.indexOf(v) === i);
      $err.textContent =
        campos.length > 0
          ? `Preencha os campos obrigatórios: ${campos.join(', ')}`
          : 'Verifique os campos do formulário.';
    }
    return;
  }

  const $btn = document.getElementById('btn-submit') as HTMLButtonElement | null;
  if ($btn) {
    $btn.disabled = true;
    $btn.textContent = 'Criando...';
  }

  try {
    const { proposta } = await api.propostas.create(parsed.data as CriarPropostaInput);
    getRouter().navigate(`/propostas/${proposta.id}`);
  } catch (err) {
    if ($btn) {
      $btn.disabled = false;
      $btn.textContent = 'Criar proposta';
    }
    if ($err) {
      $err.style.display = 'block';
      $err.textContent =
        err instanceof ApiError ? `${err.code ?? 'ERRO'}: ${err.message}` : (err as Error).message;
    }
  }
}

let stylesInjected = false;
function injectStyles(): void {
  if (stylesInjected) return;
  stylesInjected = true;
  const style = document.createElement('style');
  style.textContent = `
    .form-card { background: var(--color-surface); border: 1px solid var(--color-border); border-radius: 8px; padding: var(--sp-6); }
    .form-section { border: none; padding: 0; margin: 0 0 var(--sp-5); }
    .form-section legend { font-family: var(--font-display); font-size: var(--fs-lg); font-weight: 600; margin-bottom: var(--sp-3); padding: 0; color: var(--color-text); }
    .form-row { display: grid; gap: var(--sp-3); grid-template-columns: 1fr; margin-bottom: var(--sp-3); }
    .form-row-2 { grid-template-columns: 1fr 1fr; }
    @media (max-width: 600px) { .form-row-2 { grid-template-columns: 1fr; } }
    .item-row {
      display: grid;
      grid-template-columns: 2fr 80px 60px 110px 100px 32px;
      gap: var(--sp-2);
      align-items: center;
      padding: var(--sp-2) 0;
      border-bottom: 1px solid var(--color-border);
    }
    .item-row input {
      padding: var(--sp-2);
      border: 1px solid var(--color-border-strong);
      border-radius: 4px;
      font-size: var(--fs-sm);
    }
    .item-desc-wrap { position: relative; }
    .item-desc-wrap input { width: 100%; }
    .item-suggest {
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
      z-index: 10;
      background: var(--color-surface);
      border: 1px solid var(--color-border-strong);
      border-radius: 4px;
      box-shadow: var(--shadow-md);
      max-height: 240px;
      overflow-y: auto;
      margin-top: 2px;
    }
    .item-suggest-row {
      padding: var(--sp-2) var(--sp-3);
      cursor: pointer;
      border-bottom: 1px solid var(--color-border);
    }
    .item-suggest-row:hover { background: var(--color-surface-alt); }
    .item-suggest-row:last-child { border-bottom: none; }
    .item-suggest-empty { padding: var(--sp-3); color: var(--color-text-muted); font-size: var(--fs-sm); font-style: italic; }
    .item-suggest-desc { font-size: var(--fs-sm); color: var(--color-text); }
    .item-suggest-meta { font-size: var(--fs-xs); color: var(--color-text-muted); margin-top: 2px; }
    .item-suggest-estoque { display: inline-block; padding: 1px 6px; border-radius: 3px; margin-left: 6px; font-weight: 600; font-size: 10px; }
    .item-suggest-estoque.estoque-ok { background: #d4edda; color: #155724; }
    .item-suggest-estoque.estoque-baixo { background: #fff3cd; color: #856404; }
    .item-suggest-estoque.estoque-zero { background: #f8d7da; color: #721c24; }
    .item-desc-wrap { position: relative; }
    .item-desc-wrap input { width: 100%; }
    .item-suggest {
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
      z-index: 10;
      background: var(--color-surface);
      border: 1px solid var(--color-border-strong);
      border-radius: 4px;
      box-shadow: var(--shadow-md);
      max-height: 240px;
      overflow-y: auto;
      margin-top: 2px;
    }
  `;
  document.head.appendChild(style);
}

export { getTipoFromUrl };
